import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import type { TenantResponse } from '@/api/responses'
import { Spinner } from '@/components/ui'
import { tenantsQueryKey, useTenants } from '@/features/tenants/queries'

import { WizardShell } from './components/WizardShell'
import { ConnectWhatsAppStep } from './steps/ConnectWhatsAppStep'
import { GoLiveStep } from './steps/GoLiveStep'
import { OrganizationStep } from './steps/OrganizationStep'
import { PlaygroundStep } from './steps/PlaygroundStep'
import { useAdvanceOnboarding, useOnboardingSession } from './useOnboarding'
import { resolveWizardStep } from './wizardSteps'

export interface OnboardingWizardPageProps {
  /** Chamado quando o usuario decide sair da wizard a partir do passo "Pronto" (vai pro dashboard). */
  readonly onGoLive: (tenant: TenantResponse) => void
}

/**
 * Wizard de onboarding self-service (WP-SS-01), persistente/retomavel: criar organizacao (+ site) ->
 * EXPERIMENTAR o agente provisional no Playground (a primeira experiencia util, meta &lt; 10 min) ->
 * conectar o WhatsApp como acao explicita -> "Pronto". O passo atual e sempre derivado do backend
 * (`OnboardingSession.currentStage` + `tenant.status`), nunca de estado local do browser, entao
 * fechar e reabrir a aba retoma no lugar certo.
 */
export function OnboardingWizardPage({ onGoLive }: OnboardingWizardPageProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const tenantsQuery = useTenants()
  const tenants = tenantsQuery.data ?? []
  const tenant = tenants.at(0)

  const onboardingQuery = useOnboardingSession(tenant?.id)
  const advance = useAdvanceOnboarding(tenant?.id)
  const session = onboardingQuery.data

  const step = resolveWizardStep(tenant, session)

  function handleOrganizationCreated(created: TenantResponse): void {
    queryClient.setQueryData(tenantsQueryKey, [created])
  }

  if (tenantsQuery.isPending) {
    return (
      <main className="flex min-h-full items-center justify-center p-8">
        <Spinner label={t('loading')} />
      </main>
    )
  }

  // Aguarda a sessao de onboarding quando o tenant existe e ainda esta em onboarding (evita um flash
  // do passo errado antes de o estagio persistido chegar do backend).
  if (tenant !== undefined && tenant.status !== 'Active' && onboardingQuery.isPending) {
    return (
      <main className="flex min-h-full items-center justify-center p-8">
        <Spinner label={t('loading')} />
      </main>
    )
  }

  return (
    <WizardShell activeStep={step}>
      {step === 'organization' ? <OrganizationStep onSuccess={handleOrganizationCreated} /> : null}
      {step === 'playground' && tenant !== undefined ? (
        <PlaygroundStep
          tenant={tenant}
          onContinue={() => {
            advance.mutate({ stage: 'Channel' })
          }}
        />
      ) : null}
      {step === 'connect' && tenant !== undefined ? <ConnectWhatsAppStep tenant={tenant} /> : null}
      {step === 'goLive' && tenant !== undefined ? (
        <GoLiveStep
          tenant={tenant}
          onContinue={() => {
            // Persiste a conclusao do onboarding (estagio GoLive + GoLiveAt) e so entao navega para o
            // dashboard. `onSettled` navega mesmo em caso de erro, para nunca prender o usuario aqui.
            advance.mutate(
              { stage: 'GoLive' },
              {
                onSettled: () => {
                  onGoLive(tenant)
                },
              },
            )
          }}
        />
      ) : null}
    </WizardShell>
  )
}
